import {_} from 'lodash';
import * as ko from 'knockout';
import {ObjectTypeId} from '../../../../constants';
import {IObjectData, TSyncfield} from '../../../../index';
import {getRouteDefinition} from '../../../../constants/RouteDefinitions';
import {FilterType, IObjectSearchParams, template as tpl, viewModel as ObjectSearch} from '../../../object/search';
import {nsSearchToggle} from '../../../search/nsSearchToggle';
import ItemCheckbox = nsSearchToggle.ItemCheckbox;

export const template = tpl;

/** Syncfields required on each hit for `createoverride` (not shown as columns). */
const OVERRIDE_GROUP_SYNCFIELD_NAMES: TSyncfield[] = ['SourceId'];

export interface IAddGroupsSearchParams extends IObjectSearchParams {
    excludedGroupIds: number[];
    selectedObjects: KnockoutObservableArray<IObjectData>;
}

/** Group search with checkboxes; rows already in the membership list are visible but not selectable. */
export class viewModel extends ObjectSearch {
    private readonly excludedGroupIds: number[];

    constructor(params: IAddGroupsSearchParams) {
        super(_.defaultsDeep(params, {
            autoSelect: true,
            fnValidateRow: (obj: IObjectData) => !params.excludedGroupIds?.includes(obj.id),
            isSelectable: true,
            syncfieldNames: OVERRIDE_GROUP_SYNCFIELD_NAMES,
            __routeDef: getRouteDefinition('groupsearch', {showHeading: false}),
            __objectTypeId: ObjectTypeId.Group,
        }));
        this.excludedGroupIds = params.excludedGroupIds ?? [];
    }

    protected override getTableKeys(): string[] {
        const keys = super.getTableKeys();
        const extra = this.params.syncfieldNames ?? OVERRIDE_GROUP_SYNCFIELD_NAMES;
        return _.uniq([...keys, ...extra]);
    }

    override showTableColumn(key: string): boolean {
        if (OVERRIDE_GROUP_SYNCFIELD_NAMES.includes(key as TSyncfield)) {
            return false;
        }
        return super.showTableColumn(key);
    }

    protected override onRowClick(item: IObjectData, e: JQuery.Event) {
        if (this.params.fnValidateRow && !this.params.fnValidateRow(item)) {
            return;
        }
        this.getItemCheckbox(item).toggle();
    }

    override isFilterAvailable(filterType: FilterType) {
        if (filterType === FilterType.Deleted) {
            return false;
        }
        return super.isFilterAvailable(filterType);
    }

    override showOperationsButton(): boolean {
        return false;
    }

    protected override showSelectedList = ko.pureComputed((): boolean => false);

    private isExcluded(obj: IObjectData): boolean {
        return this.excludedGroupIds.includes(obj.id);
    }

    protected override getRowBindings(obj: IObjectData): any {
        return this.isExcluded(obj)
            ? {class: 'rowDisabled'}
            : super.getRowBindings(obj);
    }

    protected override getCellTitle(key: string, obj: IObjectData): string | number | null {
        return key !== '$derivedFromManualObject' && this.isExcluded(obj)
            ? 'user.membership.add.already.in.list'
            : super.getCellTitle(key, obj);
    }

    /** Excluded groups keep a visible row with a disabled selector checkbox. */
    protected override getItemCheckbox(item: IObjectData): ItemCheckbox<IObjectData> {
        const checkbox = super.getItemCheckbox(item);
        if (this.params.fnValidateRow && !this.params.fnValidateRow(item)) {
            checkbox.isEnabled = () => false;
            checkbox.getTitle = () => 'user.membership.add.already.in.list';
        }
        return checkbox;
    }
}
